import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { DialogComponent, ModalSizeMap } from '@kerberos-compliance/compliance-shared-ui';

@Component({
  selector: 'aad-logout-confirmation',
  templateUrl: './logout-confirmation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, DialogComponent],
})
export class LogoutConfirmationComponent {
  protected readonly ModalSizeMap = ModalSizeMap;

  private readonly navigationService = inject(NavigationService);

  private readonly isModalOpenSignal = signal<boolean>(false);

  protected readonly isModalOpen = this.isModalOpenSignal.asReadonly();

  /**
   * Opens the confirmation dialog. The session stays untouched until the user
   * confirms.
   *
   * @returns {void} Does not return a value.
   */
  public open(): void {
    this.isModalOpenSignal.set(true);
  }

  /**
   * Ends the session after the dialog was confirmed by navigating to the logout
   * page, which is what actually clears the authentication session.
   *
   * @returns {Promise<void>} Resolves once the navigation settled.
   */
  protected async confirm(): Promise<void> {
    this.isModalOpenSignal.set(false);
    await this.navigationService.goToLogout();
  }

  /**
   * Closes the dialog, leaving the session and page state untouched — covers the
   * cancel button as well as the `dismiss` the shared dialog also emits alongside
   * `confirmedClick`.
   *
   * @returns {void} Does not return a value.
   */
  protected dismiss(): void {
    this.isModalOpenSignal.set(false);
  }
}
